/**
 * Edit User Dialog — CR-5857
 *
 * Admin-driven edit of a supplier user from Suppliers → Users.
 * Visible to helix admins and customer admins only; the matching
 * server-side guard lives in CanManageUsers.has_object_permission.
 */

import { useState, useEffect } from 'react';
import { coreAPI, type UserProfile, type Role } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Switch } from '../../components/ui/switch';
import { Loader2, AlertCircle, Save, Star } from 'lucide-react';
import { toast } from 'sonner';

interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userProfile: UserProfile | null;
  onSaved: () => void;
  /** The supplier whose Users tab this was opened from. The primary-contact
   *  toggle applies to this company only. */
  companyId: number;
  companyName: string;
}

interface FormState {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  job_title: string;
  department: string;
  role: string;
}

const EMPTY_FORM: FormState = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  job_title: '',
  department: '',
  role: 'none',
};

export function EditUserDialog({
  open,
  onOpenChange,
  userProfile,
  onSaved,
  companyId,
  companyName,
}: EditUserDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Primary contact for THIS supplier only. `wasPrimary` is the persisted
  // value, so an unchanged toggle writes nothing and the assignment isn't
  // re-stamped every time the dialog is saved.
  const [isPrimary, setIsPrimary] = useState(false);
  const [wasPrimary, setWasPrimary] = useState(false);

  /** Companies this user fronts other than this one — named in the hint so it's
   *  obvious the toggle governs this supplier alone. */
  const otherCompanies = (userProfile?.primary_contact_for ?? [])
    .filter((a) => a.company !== companyId)
    .map((a) => a.company_name);

  useEffect(() => {
    if (!open || !userProfile) return;
    const holdsIt = (userProfile.primary_contact_for ?? [])
      .some((a) => a.company === companyId);
    setIsPrimary(holdsIt);
    setWasPrimary(holdsIt);
  }, [open, userProfile, companyId]);

  useEffect(() => {
    if (!open || !userProfile) return;
    setError(null);
    setForm({
      first_name: userProfile.user.first_name || '',
      last_name: userProfile.user.last_name || '',
      email: userProfile.user.email || '',
      phone: userProfile.phone || '',
      job_title: userProfile.job_title || '',
      department: userProfile.department || '',
      role: userProfile.role != null ? String(userProfile.role) : 'none',
    });
  }, [open, userProfile]);

  useEffect(() => {
    if (!open || !userProfile?.company_type) return;
    setLoadingRoles(true);
    coreAPI.getRoles({ company_type: userProfile.company_type, is_active: true })
      .then((res) => setRoles(res.data.results || []))
      .catch(() => setRoles([]))
      .finally(() => setLoadingRoles(false));
  }, [open, userProfile?.company_type]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile) return;
    // CR-7076: 'none' only occurs for a legacy roleless user whose role was
    // never picked. The server rejects roleless saves — require the choice.
    if (form.role === 'none') {
      setError('Pick a user role. Every user needs one.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await coreAPI.updateManagedUserProfile(userProfile.id, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        job_title: form.job_title.trim(),
        department: form.department.trim(),
        // CR-7076: 'none' can only occur for a legacy roleless user whose
        // role was never picked — the server rejects clearing, so block here.
        role: Number(form.role),
      });

      // Only written when the toggle actually moved. Designating takes the role
      // from whoever holds it, so the response reports who was replaced.
      if (isPrimary !== wasPrimary) {
        const res = await coreAPI.setCompanyPrimaryContact(
          companyId, isPrimary ? userProfile.id : null,
        );
        const replaced = res.data.replaced;
        if (replaced) {
          toast.success(
            `Primary contact for ${companyName} — replaced ${replaced.previous_contact}`,
          );
        } else if (isPrimary) {
          toast.success(`Set as primary contact for ${companyName}`);
        } else {
          toast.success(`Removed as primary contact for ${companyName}`);
        }
      }

      toast.success('User profile updated');
      onOpenChange(false);
      onSaved();
    } catch (err: unknown) {
      // CR-5857: backend wraps errors as { status, code, message } via the
      // shared exception handler; fall back to DRF's bare `detail` and the
      // older `error` shape just in case.
      const axiosErr = err as {
        response?: { data?: { error?: string; detail?: string; message?: string } };
      };
      const message =
        axiosErr.response?.data?.message ||
        axiosErr.response?.data?.error ||
        axiosErr.response?.data?.detail ||
        (err instanceof Error ? err.message : 'Failed to update user profile');
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (!userProfile) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit User Profile</DialogTitle>
          <DialogDescription>
            Update profile details for {userProfile.user.full_name || userProfile.user.email}.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-first-name">First Name</Label>
              <Input
                id="edit-first-name"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-last-name">Last Name</Label>
              <Input
                id="edit-last-name"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-email">Email *</Label>
            <Input
              id="edit-email"
              type="email"
              value={form.email}
              required
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-phone">Telephone</Label>
            <Input
              id="edit-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-job-title">Job Title</Label>
              <Input
                id="edit-job-title"
                value={form.job_title}
                onChange={(e) => setForm({ ...form, job_title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-department">Department</Label>
              <Input
                id="edit-department"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>User Role</Label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v })}
              disabled={loadingRoles}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingRoles ? 'Loading...' : 'Select a role'} />
              </SelectTrigger>
              <SelectContent>
                {/* CR-7076: "No specific role" removed — clearing a role
                    is rejected server-side (roleless users unsupported). */}
                {roles.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Scoped to this supplier. A user can front several companies, and
              this toggle deliberately governs only this one — the full set is
              edited in User Management. */}
          <div className="flex items-start gap-3 rounded-md border border-border p-3">
            <Switch
              id="edit-primary-contact"
              checked={isPrimary}
              onCheckedChange={setIsPrimary}
              className="mt-0.5"
            />
            <div className="space-y-1">
              <Label htmlFor="edit-primary-contact" className="flex items-center gap-1.5">
                <Star
                  className={`h-4 w-4 ${
                    isPrimary ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground'
                  }`}
                />
                Primary contact for {companyName}
              </Label>
              <p className="text-xs text-muted-foreground">
                This toggle applies to <strong>{companyName}</strong> only. Each
                company has one primary contact, so turning it on replaces whoever
                holds it for this supplier.
              </p>
              {otherCompanies.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Unaffected here: this user is also the primary contact for{' '}
                  {otherCompanies.join(', ')}. Manage those in User Management.
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
              ) : (
                <><Save className="h-4 w-4 mr-2" />Save Changes</>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
